import React from "react";
import { Check } from "lucide-react";
import { VbButton } from "@/components/vb";

export type BillingCadence = "monthly" | "annual";

/** The single V2 plan. Copy, prices and bullets are kept identical to the
 *  onboarding billing step (pages/OnboardingPage.tsx) so a user sees one offer
 *  in one shape wherever they meet it — Settings previously advertised the
 *  retired Free/Standard/Premium tiers at $10/$25, which no longer exist on the
 *  backend (`BillingAccount.PLAN_CHOICES` V2 is a single tier). */
export const PLAN_NAME = "AI Trust Signals";

export const PLAN_PRICE: Record<BillingCadence, { amount: string; per: string; cta: string }> = {
    monthly: {
        amount: "$25",
        per: "/ month · cancel any time",
        cta: "Or subscribe now: $25/mo",
    },
    annual: {
        amount: "$250",
        per: "/ year · 2 months free",
        cta: "Subscribe now: $250/yr",
    },
};

export const PLAN_FEATURES = [
    "Your score, plus the list of what's holding it down",
    "MarcusAI tells you what to fix first",
    "See how ChatGPT, Gemini and Claude each describe you",
    "Test every type of buyer you sell to",
    "A fresh report every month, so you can watch your score climb",
];

type Props = {
    cadence: BillingCadence;
    onCadenceChange: (cadence: BillingCadence) => void;
    /** Label for the trial CTA — the 10/30-day cohort comes from the backend. */
    trialLabel: string;
    /** Trial checkout. Omitted (with the annual note shown instead) on annual. */
    onStartTrial: () => void;
    /** Direct subscribe checkout for the selected cadence. */
    onSubscribe: () => void;
    busy?: boolean;
};

/** The plan offer: cadence toggle + the recommended plan card.
 *
 *  The trial only applies to monthly billing, mirroring onboarding — on annual
 *  the trial CTA is replaced by a note saying where to find it. */
export function PlanCard({
    cadence,
    onCadenceChange,
    trialLabel,
    onStartTrial,
    onSubscribe,
    busy,
}: Props) {
    const isAnnual = cadence === "annual";
    const price = PLAN_PRICE[cadence];

    return (
        <div className="vb-plan-single">
            <div className="vb-billtoggle" role="group" aria-label="Billing period">
                <button
                    type="button"
                    aria-pressed={!isAnnual}
                    onClick={() => onCadenceChange("monthly")}
                >
                    Monthly
                </button>
                <button
                    type="button"
                    aria-pressed={isAnnual}
                    onClick={() => onCadenceChange("annual")}
                >
                    Annual · 2 months free
                </button>
            </div>

            <div className="vb-plan vb-plan--rec">
                <span className="vb-plan__rec-pill">Full access</span>
                <span className="vb-plan__name">{PLAN_NAME}</span>
                <div className="vb-plan__price">
                    <span className="vb-plan__amt">{price.amount}</span>
                    <span className="vb-plan__per">{price.per}</span>
                </div>

                <ul>
                    {PLAN_FEATURES.map((feature) => (
                        <li key={feature}>
                            <Check size={14} strokeWidth={3} aria-hidden="true" />
                            {feature}
                        </li>
                    ))}
                </ul>

                <div className="vb-plan__cta">
                    {isAnnual ? (
                        <>
                            <VbButton
                                type="button"
                                size="lg"
                                block
                                onClick={onSubscribe}
                                disabled={busy}
                            >
                                {price.cta}
                            </VbButton>
                            <p className="vb-plan__note">
                                Free trial available on monthly billing.
                            </p>
                        </>
                    ) : (
                        <>
                            <VbButton
                                type="button"
                                size="lg"
                                block
                                onClick={onStartTrial}
                                disabled={busy}
                            >
                                {trialLabel}
                            </VbButton>
                            <p className="vb-plan__note">
                                Full access today. Your card is not charged until the trial ends.
                                Cancel before then and you pay nothing.
                            </p>
                            <VbButton
                                type="button"
                                variant="secondary"
                                size="lg"
                                block
                                onClick={onSubscribe}
                                disabled={busy}
                            >
                                {price.cta}
                            </VbButton>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PlanCard;
